import { isEqual, uniqWith } from "lodash";
import { createContext, ReactNode, useContext, useMemo } from "react";
import { createMongoAbility, MongoAbility, RawRuleOf } from '@casl/ability'
import { useGetLicensesQuery } from "@/core/services/license";
import { selectAuthUser } from "@/redux/reducers/auth.reducer";
import { useAppSelector } from "@/redux/rootReducer";

export type Action =
  |'manage'
  |'create'
  |'read'
  |'list'
  |'update'
  |'delete'

const subjects = [
  'Admin',
  'CustomDevice',
  'Consultation',
  'Etablissement',
  'Expertise',
  'MeetingLink',
  'Patient',
  'Staff',
  'FormTemplate',
  'FormSubmission',
  'Statistics',
] as const

export type Subject = (typeof subjects)[number]

export type Abilities = [Action, Subject]

export type AppAbility = MongoAbility<Abilities>

const AbilityContext = createContext<AppAbility | null>(null)

export function AbilityProvider({children}: { children: ReactNode }) {
  const authUser = useAppSelector(selectAuthUser)

  const { data: licenses } = useGetLicensesQuery({ id: authUser?.licenses }, { skip: !authUser?.licenses.length })

  const ability = useMemo(() => {
    const permissions = uniqWith(licenses?.map(license => license.policies.map(policy => policy.permissions).flat()).flat(), isEqual)

    const rules: RawRuleOf<AppAbility>[] = permissions.map((permission) => ({
      action: permission.action as Action[],
      subject: permission.resource as Subject[],
    }))

    return createMongoAbility<AppAbility>(rules)
  }, [licenses])
  
  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  )
}

export function useAbility() {
  const context = useContext(AbilityContext)
  
  if (!context) {
    throw new Error(
      'useAbility must be used within a AbilityProvider'
    );
  }

  return context
}